package fr.frux.feature.treedetail.identity

import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.feature.treedetail.TreeDetailState
import fr.frux.shared.content.DescriptionLength
import fr.frux.shared.content.TreeCorrectionLimits
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.TreeCorrection
import fr.frux.shared.domain.model.undoFrom
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeCorrectionRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val TAG = "TreeIdentity"
private const val SEARCH_DEBOUNCE_MS = 300L

/**
 * Corrections du bloc Identité (espèce, note), « Annuler » et historique, extraits de
 * `TreeDetailViewModel` (ratchet detekt LargeClass), même patron que `TreeDetailModerationController`.
 *
 * Une clé d'idempotence est tirée à l'ouverture de chaque feuille et gardée pour tous ses
 * « Réessayer ». Le message de la snackbar ne vient que de `corrections.applied` : une correction
 * faite ailleurs (l'écran de déplacement) s'annonce ici de la même façon.
 */
class TreeIdentityController internal constructor(
    private val treeId: Long,
    private val corrections: TreeCorrectionRepository,
    private val speciesRepository: SpeciesRepository,
    private val scope: CoroutineScope,
    private val state: MutableStateFlow<TreeDetailState>,
    private val isLoggedIn: () -> Boolean,
    private val onLoginRequired: () -> Unit,
    private val reload: () -> Unit,
    private val newKey: () -> String,
) {
    private var referential: List<Species> = emptyList()
    private var sheetKey: String = ""
    private var searchJob: Job? = null

    /** Champ de la dernière annulation envoyée, l'évènement d'une annulation ne portant pas d'inverse. */
    private var undoneField: IdentityField? = null

    init {
        scope.launch {
            speciesRepository
                .getSpecies()
                .onSuccess { referential = it }
                .onFailure { Logger.d(TAG, "Référentiel espèces indisponible : ${it.message}") }
        }
        scope.launch {
            corrections.applied.filter { it.treeId == treeId }.collect { applied ->
                val undo = applied.undo
                val field = undo?.field ?: undoneField
                val message = if (undo != null) CorrectionMessage.Saved(undo.field, undo) else CorrectionMessage.Undone
                state.updateIdentity { it.copy(message = message, highlighted = field, correctedByMe = true) }
                reload()
            }
        }
    }

    fun openSpecies() {
        val tree = state.value.tree ?: return
        if (!isLoggedIn()) return onLoginRequired()
        sheetKey = newKey()
        val sheet =
            IdentitySheet.Species(
                query = "",
                results = emptyList(),
                species = referential.find { it.id == tree.speciesId },
                certainty = tree.certainty,
                reason = "",
                saving = false,
                error = null,
                canSave = false,
            )
        state.updateIdentity { it.copy(sheet = sheet.refreshed(tree.identity())) }
    }

    fun onSpeciesQueryChange(query: String) {
        edit<IdentitySheet.Species> { it.copy(query = query, results = emptyList()) }
        searchJob?.cancel()
        if (query.trim().length < MIN_SPECIES_QUERY_LENGTH) return
        searchJob =
            scope.launch {
                delay(SEARCH_DEBOUNCE_MS)
                val results = searchSpecies(referential, query)
                edit<IdentitySheet.Species> { if (it.query == query) it.copy(results = results) else it }
            }
    }

    fun selectSpecies(species: Species) {
        searchJob?.cancel()
        edit<IdentitySheet.Species> { it.copy(species = species, query = "", results = emptyList()) }
    }

    fun clearSpecies() = edit<IdentitySheet.Species> { it.copy(species = null) }

    fun selectCertainty(certainty: Certainty) = edit<IdentitySheet.Species> { it.copy(certainty = certainty) }

    fun onSpeciesReasonChange(reason: String) =
        edit<IdentitySheet.Species> { it.copy(reason = reason.take(TreeCorrectionLimits.REASON_MAX)) }

    fun saveSpecies() {
        val sheet = state.value.identity.sheet as? IdentitySheet.Species
        val correction = sheet?.takeIf { it.canSave }?.toCorrection() ?: return
        save(correction)
    }

    fun openNote() {
        val tree = state.value.tree ?: return
        if (!isLoggedIn()) return onLoginRequired()
        sheetKey = newKey()
        val sheet =
            IdentitySheet.Note(
                text = tree.description.orEmpty(),
                reason = "",
                saving = false,
                error = null,
                canSave = false,
            )
        state.updateIdentity { it.copy(sheet = sheet.refreshed(tree.identity())) }
    }

    fun onNoteChange(text: String) = edit<IdentitySheet.Note> { it.copy(text = text.take(DescriptionLength.MAX)) }

    fun onNoteReasonChange(reason: String) =
        edit<IdentitySheet.Note> { it.copy(reason = reason.take(TreeCorrectionLimits.REASON_MAX)) }

    fun saveNote() {
        val sheet = state.value.identity.sheet as? IdentitySheet.Note ?: return
        if (sheet.canSave) save(sheet.toCorrection())
    }

    /** Ouvert à tous, visiteur anonyme compris : l'historique est public. */
    fun openHistory() {
        state.updateIdentity { it.copy(sheet = IdentitySheet.History(HistoryLoad.Loading)) }
        retryHistory()
    }

    fun retryHistory() {
        edit<IdentitySheet.History> { IdentitySheet.History(HistoryLoad.Loading) }
        scope.launch {
            val load =
                corrections.revisions(treeId).fold(
                    onSuccess = { log ->
                        val rows =
                            historyRows(log, { id -> referential.find { it.id == id } }, state.value.tree?.importSource)
                        HistoryLoad.Loaded(rows, log.truncated)
                    },
                    onFailure = { error ->
                        Logger.d(TAG, "Historique indisponible pour l'arbre $treeId : ${error.message}")
                        HistoryLoad.Failed(error.asAppError())
                    },
                )
            edit<IdentitySheet.History> { IdentitySheet.History(load) }
        }
    }

    /** Vrai si un compte est connecté : l'écran ouvre alors le déplacement. Sinon, l'invite. */
    fun requestMove(): Boolean {
        if (isLoggedIn()) return true
        onLoginRequired()
        return false
    }

    fun dismissSheet() {
        searchJob?.cancel()
        // Invalide la clé : un succès tardif ne doit pas fermer une autre feuille ouverte ensuite.
        sheetKey = ""
        state.updateIdentity { it.copy(sheet = null) }
    }

    /** Envoie l'inverse de la dernière correction, avec sa propre clé et sans nouvel « Annuler ». */
    fun undo() {
        val saved = state.value.identity.message as? CorrectionMessage.Saved ?: return
        undoneField = saved.field
        state.updateIdentity { it.copy(message = null) }
        scope.launch {
            corrections.correct(treeId, saved.undo, undo = null, idempotencyKey = newKey()).onFailure { error ->
                Logger.e(TAG, "Annulation de la correction refusée", error)
                val failed = CorrectionMessage.UndoFailed(error.asAppError())
                state.updateIdentity { it.copy(message = failed) }
            }
        }
    }

    fun consumeMessage() {
        state.updateIdentity { it.copy(message = null, highlighted = null) }
    }

    /** N'agit que si la feuille ouverte est du type attendu, puis recalcule `canSave`. */
    private inline fun <reified T : IdentitySheet> edit(crossinline transform: (T) -> IdentitySheet) {
        state.update { current ->
            val sheet = current.identity.sheet as? T ?: return@update current
            val identity = current.tree?.identity()
            current.copy(identity = current.identity.copy(sheet = transform(sheet).refreshed(identity)))
        }
    }

    /** Succès : feuille fermée, le message arrive par `applied`. Échec : feuille gardée, même clé au prochain essai. */
    private fun save(correction: TreeCorrection) {
        val identity = state.value.tree?.identity() ?: return
        val key = sheetKey
        edit<IdentitySheet> { it.withSaving(saving = true, error = null) }
        scope.launch {
            corrections.correct(treeId, correction, undo = correction.undoFrom(identity), idempotencyKey = key).fold(
                onSuccess = { if (sheetKey == key) dismissSheet() },
                onFailure = { error ->
                    Logger.e(TAG, "Correction refusée pour l'arbre $treeId", error)
                    val current = sheetKey == key
                    if (current) {
                        edit<IdentitySheet> { it.withSaving(saving = false, error = error.asAppError()) }
                    }
                },
            )
        }
    }
}

private fun IdentitySheet.withSaving(
    saving: Boolean,
    error: AppError?,
): IdentitySheet =
    when (this) {
        is IdentitySheet.Species -> copy(saving = saving, error = error)
        is IdentitySheet.Note -> copy(saving = saving, error = error)
        is IdentitySheet.History -> this
    }

private fun MutableStateFlow<TreeDetailState>.updateIdentity(transform: (IdentityUi) -> IdentityUi) =
    update { it.copy(identity = transform(it.identity)) }
